import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { aiAssessmentAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Bot, Edit, Trash2, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';

export const AIAssessmentTable = ({ assessments, isDPO, getOrgName, onRefresh }) => {
  const navigate = useNavigate();
  const { isComplianceReadOnly } = useAuth();

  if (!assessments.length) return null;

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Bot className="h-4 w-4 text-purple-500" />
          AI System Assessments (NIST AI RMF)
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reference</TableHead>
              {isDPO && <TableHead>Organization</TableHead>}
              <TableHead>System Name</TableHead>
              <TableHead>AI Trust</TableHead>
              <TableHead>DP Impact</TableHead>
              <TableHead>Composite</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assessments.map((ai) => (
              <TableRow
                key={ai.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/risk/ai/${ai.id}`)}
              >
                <TableCell className="font-mono text-sm">{ai.ai_reference || '-'}</TableCell>
                {isDPO && <TableCell>{ai.entity_name || getOrgName(ai.organization_id)}</TableCell>}
                <TableCell className="font-medium">{ai.system_name || 'Untitled'}</TableCell>
                <TableCell>
                  <span className="text-blue-600 font-medium">
                    {ai.residual_trustworthiness_score !== null ? Math.round(ai.residual_trustworthiness_score) :
                     ai.initial_trustworthiness_score !== null ? Math.round(ai.initial_trustworthiness_score) : '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-purple-600 font-medium">
                    {ai.residual_data_protection_score !== null ? Math.round(ai.residual_data_protection_score) :
                     ai.initial_data_protection_score !== null ? Math.round(ai.initial_data_protection_score) : '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={
                    (ai.residual_risk_score || ai.initial_risk_score) > 75 ? 'text-red-500 font-medium' :
                    (ai.residual_risk_score || ai.initial_risk_score) > 50 ? 'text-orange-500' :
                    (ai.residual_risk_score || ai.initial_risk_score) > 25 ? 'text-amber-500' : 'text-green-500'
                  }>
                    {ai.residual_risk_score !== null ? Math.round(ai.residual_risk_score) :
                     ai.initial_risk_score !== null ? Math.round(ai.initial_risk_score) : '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge variant={
                    ai.status === 'approved' ? 'success' :
                    ai.status === 'rejected' ? 'destructive' :
                    ai.status === 'pending_review' ? 'warning' : 'secondary'
                  }>
                    {ai.status?.replace('_', ' ')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!isComplianceReadOnly && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/risk/ai/${ai.id}`); }}>
                          <Edit className="h-4 w-4 mr-2" />Edit
                        </DropdownMenuItem>
                      )}
                      {!isComplianceReadOnly && (
                        <DropdownMenuItem
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this AI Assessment?')) {
                              try {
                                await aiAssessmentAPI.delete(ai.id);
                                toast.success('AI Assessment deleted');
                                onRefresh();
                              } catch { toast.error('Failed to delete'); }
                            }
                          }}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
